import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2, FilePen, HeartHandshake, Plus, Trash2 } from 'lucide-react';
import { useCurrentUser } from '@/api/auth';
import { useCreerFiche, useFichesSociales, useSupprimerFiche, type FicheResume } from '@/api/social';
import OptionInactive from './OptionInactive';

const dateHeure = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export default function SocialListPage() {
  const { data: user } = useCurrentUser();
  if (!user?.options?.gestionSociale) return <OptionInactive />;
  return <Liste />;
}

function Liste() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useFichesSociales();
  const creer = useCreerFiche();

  const nouvelle = () => creer.mutate(undefined, { onSuccess: (f) => navigate(`/gestion-sociale/${f.id}`) });

  return (
    <div className="flex-1 overflow-auto bg-slate-100">
      {/* Bandeau d'accueil */}
      <div className="relative overflow-hidden bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 py-8 text-white">
        <div className="absolute -right-16 -top-20 w-72 h-72 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute right-40 -bottom-24 w-56 h-56 rounded-full bg-cyan-300/20 blur-2xl" />
        <div className="relative max-w-5xl mx-auto px-8 flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.16em]">
              <HeartHandshake size={12} /> Option Premium
            </div>
            <h1 className="mt-3 text-[26px] font-semibold tracking-tight">Gestion Sociale</h1>
            <p className="mt-1 max-w-xl text-[13.5px] text-white/85 leading-relaxed">
              Remplissez à l’écran la fiche d’identification de chaque nouveau salarié : le PDF est généré pour vous,
              prêt à partir au service social. À retourner <b className="text-white">5 jours ouvrés avant l’embauche</b>.
            </p>
          </div>
          <button
            onClick={nouvelle}
            disabled={creer.isPending}
            className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 text-[14px] font-semibold text-teal-700 shadow-lg shadow-black/10 hover:bg-emerald-50 disabled:opacity-60 transition"
          >
            <Plus size={17} strokeWidth={2.5} /> Nouvelle fiche salarié
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-8 py-6">
        {creer.isError && (
          <p className="mb-4 text-[13px] text-danger">{(creer.error as Error).message}</p>
        )}
        {error && (
          <div className="mb-4 rounded-lg border border-danger/30 bg-danger-bg p-3 text-[13px] text-danger flex items-center gap-2">
            <AlertCircle size={14} /> {(error as Error).message}
          </div>
        )}
        {isLoading && <p className="text-[13px] text-slate-500">Chargement…</p>}

        {data && data.forms.length === 0 && (
          <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-white p-12 text-center">
            <FilePen size={28} className="mx-auto text-emerald-500" />
            <div className="mt-3 text-[15px] font-semibold text-slate-900">Aucune fiche pour l’instant</div>
            <p className="mt-1 text-[13px] text-slate-600">Une embauche en vue ? Commencez sa fiche, elle s’enregistre au fil de la saisie.</p>
          </div>
        )}

        {data && data.forms.length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Fiches d’identification ({data.forms.length})
            </div>
            <ul className="divide-y divide-slate-100">
              {data.forms.map((f) => (
                <LigneFiche key={f.id} f={f} />
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function LigneFiche({ f }: { f: FicheResume }) {
  const navigate = useNavigate();
  const supprimer = useSupprimerFiche();
  const [confirmer, setConfirmer] = useState(false);

  return (
    <li className="px-5 py-3.5 flex items-center gap-4 hover:bg-slate-50">
      <button onClick={() => navigate(`/gestion-sociale/${f.id}`)} className="flex-1 min-w-0 flex items-center gap-3 text-left">
        <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
          <FilePen size={17} />
        </div>
        <div className="min-w-0">
          <div className="text-[14px] font-medium text-slate-900 truncate">
            {f.label || <span className="italic text-slate-400">Fiche sans nom</span>}
          </div>
          <div className="text-[12px] text-slate-500">Modifiée le {dateHeure(f.updatedAt)}</div>
        </div>
      </button>

      {f.status === 'SENT' && f.sentAt ? (
        <span
          className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-[11.5px] font-medium text-emerald-800"
          title={f.sentToEmail ?? undefined}
        >
          <CheckCircle2 size={12} /> Envoyée le {new Date(f.sentAt).toLocaleDateString('fr-FR')}
        </span>
      ) : (
        <span className="hidden sm:inline-flex rounded-full bg-amber-50 border border-amber-200 px-2.5 py-1 text-[11.5px] font-medium text-amber-800">
          Brouillon
        </span>
      )}

      {confirmer ? (
        <span className="flex items-center gap-1.5">
          <button
            onClick={() => supprimer.mutate(f.id)}
            disabled={supprimer.isPending}
            className="rounded-lg bg-danger px-2.5 py-1.5 text-[12px] font-medium text-white hover:brightness-110"
          >
            Supprimer
          </button>
          <button onClick={() => setConfirmer(false)} className="rounded-lg px-2.5 py-1.5 text-[12px] text-slate-600 hover:bg-slate-100">
            Annuler
          </button>
        </span>
      ) : (
        <button
          onClick={() => setConfirmer(true)}
          title="Supprimer la fiche"
          className="p-1.5 rounded-lg text-slate-400 hover:bg-danger-bg hover:text-danger"
        >
          <Trash2 size={15} />
        </button>
      )}
    </li>
  );
}
